import { useState, type FormEvent } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { ErrorState } from '../../components/common';
import { migrationService, type ImportReport, type ImportResult, type ReportItem, type ScopeView } from '../../services/migration';
import { buildImportRequest, type DeviceMode } from './logic';
import type { ImportRequest } from '../../services/migration';

/** Import: a dry-run report first (nothing is created), then create what maps — silently. */
export function ImportPanel({ scope }: { scope: ScopeView }) {
  const { tenantId } = useTenant();
  const [mode, setMode] = useState<DeviceMode>('all');
  const [idsText, setIdsText] = useState('');
  const [includeWebsites, setIncludeWebsites] = useState(false);
  const [collectorMapText, setCollectorMapText] = useState('');
  const [clientId, setClientId] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  // What was PREVIEWED: "Create" applies exactly this request (never whatever the form says now).
  const [previewed, setPreviewed] = useState<ImportRequest | null>(null);
  const [batchCreated, setBatchCreated] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  /** Any edit to the form invalidates the preview: what is on screen no longer describes what would run. */
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setResult(null);
    setPreviewed(null);
    setBatchCreated(false);
  };

  const preview = async (after?: { afterDeviceId?: number; afterWebsiteId?: number }) => {
    if (!tenantId) return;
    setError(null);
    setBusy(true);
    try {
      const req = buildImportRequest({ dryRun: true, mode, idsText, includeWebsites, collectorMapText, clientId, ...after });
      setResult(await migrationService.importScope(tenantId, scope.id, req));
      setPreviewed(req);
      setBatchCreated(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const create = async () => {
    if (!tenantId || !previewed) return;
    setError(null);
    setBusy(true);
    try {
      const res = await migrationService.importScope(tenantId, scope.id, { ...previewed, dryRun: false });
      setResult(res);
      setBatchCreated(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void preview();
  };

  const report = result?.report;
  const canApply = report !== undefined && result?.dryRun === true && previewed !== null && (result.plannedResources ?? 0) > 0;
  const capped = report !== undefined && (report.cappedDevices || report.cappedWebsites);
  return (
    <details open className="card" aria-label="Import">
      <summary><h3 style={{ display: 'inline' }}>Import from LogicMonitor</h3></summary>
      <form onSubmit={submit}>
        <fieldset className="form-field">
          <legend>Devices</legend>
          {(['all', 'groups', 'ids', 'none'] as const).map((m) => (
            <label key={m} className="checkbox-label">
              <input type="radio" name={`imp-mode-${scope.id}`} checked={mode === m} onChange={() => edit(setMode)(m)} />
              {m === 'all' ? 'All devices' : m === 'groups' ? 'Device groups (ids)' : m === 'ids' ? 'Specific devices (ids)' : 'None (websites only)'}
            </label>
          ))}
        </fieldset>
        {(mode === 'groups' || mode === 'ids') && (
          <div className="form-field">
            <label htmlFor={`imp-ids-${scope.id}`}>LogicMonitor ids (comma separated)</label>
            <input id={`imp-ids-${scope.id}`} value={idsText} onChange={(e) => edit(setIdsText)(e.target.value)} />
          </div>
        )}
        <label className="checkbox-label">
          <input type="checkbox" checked={includeWebsites} onChange={(e) => edit(setIncludeWebsites)(e.target.checked)} />
          Include websites
        </label>
        <div className="form-field">
          <label htmlFor={`imp-map-${scope.id}`}>Collector map (LogicMonitor collector id = Detect collector id, one per line)</label>
          <textarea id={`imp-map-${scope.id}`} rows={3} value={collectorMapText} onChange={(e) => edit(setCollectorMapText)(e.target.value)} placeholder="94 = col_abc123" />
          <span className="field-hint">Devices behind an unmapped collector wait: they are reported, never dropped.</span>
        </div>
        <div className="form-field">
          <label htmlFor={`imp-client-${scope.id}`}>Client id (optional)</label>
          <input id={`imp-client-${scope.id}`} value={clientId} onChange={(e) => edit(setClientId)(e.target.value)} />
        </div>
        <div className="page-actions">
          <button type="submit" className="btn btn-secondary" disabled={busy}>Preview (changes nothing)</button>
          {canApply && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Create ${result?.plannedResources} resource(s) in Detect? They are held silent by this scope.`)) void create();
              }}
            >
              Create {result?.plannedResources} resource(s)
            </button>
          )}
        </div>
      </form>
      {error !== null && <ErrorState error={error} />}
      {result && !result.dryRun && result.applied && (
        <div className="notice" role="status">
          Created {result.applied.created}, already imported {result.applied.alreadyImported}
          {result.applied.failed.length > 0 && <>, <strong>{result.applied.failed.length} failed</strong></>}.
          {result.applied.failed.length > 0 && (
            <ul>
              {result.applied.failed.map((f) => (
                <li key={`${f.lm.kind}-${f.lm.id}`}>{f.lm.kind} {f.lm.id}: {f.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {report && (
        <ReportView
          report={report}
          // A capped run is done in batches: the batch on screen is created first, so no batch is ever skipped.
          onContinue={capped && (batchCreated || (result?.plannedResources ?? 0) === 0) ? (a) => void preview(a) : undefined}
          continueHint={capped && !(batchCreated || (result?.plannedResources ?? 0) === 0) ? 'Create this batch first; then you can preview the next one.' : undefined}
        />
      )}
    </details>
  );
}

function ItemTable({ title, items }: { title: string; items: ReportItem[] }) {
  if (items.length === 0) return null;
  return (
    <>
      <h4>{title}</h4>
      <div className="table-scroll">
        <table className="data-table">
          <thead><tr><th>LM id</th><th>Name</th><th>Status</th><th>Notes</th></tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.lmId}>
                <td>{i.lmId}</td>
                <td>{i.name}</td>
                <td>
                  <span className={`status-badge ${i.status === 'ready' ? 'status-active' : i.status === 'unmapped' ? 'status-down' : 'status-pending'}`}>{i.status}</span>
                  {i.reason && <div className="text-muted">{i.reason}</div>}
                  {i.status === 'waiting-for-collector' && <div className="text-muted">LM collector {i.lmCollectorId ?? 'unknown'} is not mapped</div>}
                </td>
                <td>{i.notes.length > 0 ? <ul>{i.notes.map((n, k) => <li key={k}>{n}</li>)}</ul> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ReportView({ report, onContinue, continueHint }: { report: ImportReport; onContinue?: (a: { afterDeviceId?: number; afterWebsiteId?: number }) => void; continueHint?: string }) {
  const s = report.summary;
  return (
    <div aria-label="Import report">
      <h4>Report</h4>
      <p>
        {s.devicesSelected ?? 0} device(s) selected of {s.devicesInLm ?? 0} in LogicMonitor: <strong>{s.devicesReady ?? 0} ready</strong>,{' '}
        {s.devicesWaitingForCollector ?? 0} waiting for a collector, {s.devicesUnmapped ?? 0} unmapped.
        {s.websitesInLm !== null && s.websitesInLm !== undefined && <> Websites: {s.websitesReady ?? 0} ready, {s.websitesUnmapped ?? 0} unmapped.</>}
      </p>
      <p>
        DataSources applied: {s.dataSourcesApplied ?? 0} ({s.dataSourcesToLibrary ?? 0} map to the library, {s.dataSourcesProposed ?? 0} proposed,{' '}
        {s.dataSourcesUnmapped ?? 0} unmapped — reported, not installed). Alert rules: {s.alertRules ?? 0} ({s.alertRulesWithBlockers ?? 0} with blockers); chains: {s.alertChains ?? 0} ({s.alertChainsWithBlockers ?? 0} with blockers).
      </p>
      {report.missingDeviceIds.length > 0 && <p role="alert">LogicMonitor did not return device id(s): {report.missingDeviceIds.join(', ')}.</p>}
      {(report.cappedDevices || report.cappedWebsites) && (
        <p className="notice">
          This run was capped to a batch. {continueHint && <>{continueHint} </>}{onContinue && (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => onContinue({
                ...(report.cappedDevices && report.nextAfterDeviceId !== null ? { afterDeviceId: report.nextAfterDeviceId } : {}),
                ...(report.cappedWebsites && report.nextAfterWebsiteId !== null ? { afterWebsiteId: report.nextAfterWebsiteId } : {}),
              })}
            >
              Preview the next batch
            </button>
          )}
        </p>
      )}
      <ItemTable title="Devices" items={report.devices} />
      <ItemTable title="Websites" items={report.websites} />
      {report.dataSources.length > 0 && (
        <>
          <h4>DataSources</h4>
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Method</th><th>Devices</th><th>Outcome</th></tr></thead>
              <tbody>
                {report.dataSources.map((d) => (
                  <tr key={d.lmDataSourceId}>
                    <td>{d.name}</td>
                    <td>{d.collectMethod}</td>
                    <td>{d.appliedToDevices}</td>
                    <td>{d.outcome}{d.reason && <div className="text-muted">{d.reason}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {(report.routing.rules.some((r) => r.blockers.length > 0) || report.routing.chains.some((c) => c.blockers.length > 0)) && (
        <>
          <h4>Alert routing that needs a person</h4>
          <ul>
            {report.routing.rules.filter((r) => r.blockers.length > 0).map((r) => (
              <li key={`r${r.lmRuleId}`}>Rule {r.lmRuleId}: {r.blockers.join('; ')}</li>
            ))}
            {report.routing.chains.filter((c) => c.blockers.length > 0).map((c) => (
              <li key={`c${c.lmChainId}`}>Escalation chain {c.lmChainId}: {c.blockers.join('; ')}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
